import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { FdsCaseView } from "@kkp/api-client";
import {
  PageContainer,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Button,
} from "@kkp/ui";
import { formatCurrency, pageCount } from "@kkp/app-kit";
import { FdsInactiveBanner, useCapabilities } from "@kkp/features";
import { listFdsCases, type FdsQueueView } from "../../api/fds";
import { deviationLabel, reasonLabel } from "./reasons";
import { AccessAlertsSection } from "./AccessAlerts";
import { OverdueBadge, ScopePill, StagePill } from "./shared";

const PAGE_SIZE = 20;
// ponytail: the live queue is fetched in one page up to the server's clamp
// (200); metrics + rows are exact under that count. Add real pagination for
// "live" only if the operational queue regularly exceeds it.
const LIVE_LIMIT = 200;

type FdsTab = "cases" | "alerts";

export default function FdsQueuePage() {
  const [tab, setTab] = React.useState<FdsTab>("cases");
  const { data: capabilities } = useCapabilities();

  return (
    <PageContainer
      title={
        tab === "cases"
          ? "Kasus yang memerlukan peninjauan"
          : "Peringatan akses"
      }
      description={
        tab === "cases"
          ? "Keputusan petugas dan kontak PIC dicatat melalui proses back-office sesuai SOP."
          : "Login PIC dari negara berbeda dari sebelumnya. Informasi saja — tidak ada tindakan atau penahanan."
      }
      width="full"
      actions={
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={tab === "cases" ? "primary" : "secondary"}
            onClick={() => setTab("cases")}
          >
            Kasus
          </Button>
          <Button
            size="sm"
            variant={tab === "alerts" ? "primary" : "secondary"}
            onClick={() => setTab("alerts")}
          >
            Peringatan akses
          </Button>
        </div>
      }
    >
      <FdsInactiveBanner fds={capabilities?.fds} />
      {tab === "cases" ? <FdsCasesPanel /> : <AccessAlertsSection />}
    </PageContainer>
  );
}

function FdsCasesPanel() {
  const [view, setView] = React.useState<FdsQueueView>("live");
  const [offset, setOffset] = React.useState(0);
  const limit = view === "live" ? LIVE_LIMIT : PAGE_SIZE;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["fds-cases", view, offset, limit],
    queryFn: () => listFdsCases(view, limit, offset),
    refetchOnMount: "always",
  });

  const items = data?.items ?? [];
  const total = BigInt(data?.total ?? 0);
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = pageCount(total, PAGE_SIZE);

  const setViewMode = (next: FdsQueueView) => {
    setView(next);
    setOffset(0);
  };

  const batchHeld = items.filter((c) => c.scope === "WholeBatch").length;
  const recipientHeld = items.filter((c) => c.scope === "Recipient").length;

  return (
    <>
      <div className="grid gap-4">
        {view === "live" ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Card className="grid gap-1">
              <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                Kasus terbuka
              </div>
              <div className="text-2xl font-bold text-white">
                {String(total)}
              </div>
            </Card>
            <Card className="grid gap-1">
              <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                Instruksi batch tertahan
              </div>
              <div className="text-2xl font-bold text-white">{batchHeld}</div>
            </Card>
            <Card className="grid gap-1">
              <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
                Instruksi penerima tertahan
              </div>
              <div className="text-2xl font-bold text-white">
                {recipientHeld}
              </div>
            </Card>
          </div>
        ) : null}

        <Card>
          <CardHeader className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle as="h2">Antrean peninjauan</CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={view === "live" ? "primary" : "secondary"}
                onClick={() => setViewMode("live")}
              >
                Live
              </Button>
              <Button
                size="sm"
                variant={view === "all" ? "primary" : "secondary"}
                onClick={() => setViewMode("all")}
              >
                Semua
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {isError ? (
              <div className="text-sm text-[color:var(--kkp-text-muted)]">
                Gagal memuat antrean FDS.
              </div>
            ) : null}
            {isLoading ? (
              <div className="text-sm text-[color:var(--kkp-text-muted)]">
                Memuat…
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[color:var(--kkp-border)] text-left text-xs text-[color:var(--kkp-text-muted)]">
                    <th className="py-2 pr-3">Kasus / cakupan</th>
                    <th className="py-2 pr-3">Perusahaan</th>
                    <th className="py-2 pr-3">Alasan</th>
                    <th className="py-2 pr-3 text-right">Dana tertahan</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <QueueRow key={c.id} item={c} />
                  ))}
                  {!isLoading && items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-3 text-center text-sm text-[color:var(--kkp-text-muted)]"
                      >
                        Tidak ada kasus.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            {view === "all" ? (
              <div className="flex items-center justify-between pt-2 text-xs text-[color:var(--kkp-text-muted)]">
                <span>
                  Halaman {page} / {String(totalPages)} · {String(total)} kasus
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  >
                    Sebelumnya
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={BigInt(offset + PAGE_SIZE) >= total}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  >
                    Berikutnya
                  </Button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function QueueRow({ item }: { item: FdsCaseView }) {
  const navigate = useNavigate();
  return (
    <tr className="border-b border-[color:var(--kkp-border)] align-top text-sm text-[color:var(--kkp-text)]">
      <td className="py-2 pr-3">
        <div className="font-semibold">{item.bulkTxId}</div>
        <div className="mt-1">
          <ScopePill scope={item.scope} />
        </div>
        {item.scope === "Recipient" ? (
          <div className="mt-1 text-xs text-[color:var(--kkp-text-muted)]">
            {item.recipientName} · {item.employeeId}
          </div>
        ) : null}
      </td>
      <td className="py-2 pr-3">{item.companyName}</td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap gap-1">
          {item.reasons.map((r) => (
            <span
              key={r}
              className="rounded-full border border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.04)] px-2 py-0.5 text-[11px]"
            >
              {reasonLabel(r)}
            </span>
          ))}
        </div>
        {item.complianceReview ? (
          <div className="mt-1 text-[11px] font-semibold text-amber-200">
            Tinjauan kepatuhan
          </div>
        ) : null}
        {item.approvedPrincipalIdr !== null &&
        item.observedPrincipalIdr !== null ? (
          <div className="mt-1 text-[11px] text-[color:var(--kkp-text-muted)]">
            {formatCurrency(item.observedPrincipalIdr)} vs{" "}
            {formatCurrency(item.approvedPrincipalIdr)}
            {deviationLabel(
              item.observedPrincipalIdr,
              item.approvedPrincipalIdr,
            )
              ? ` (${deviationLabel(item.observedPrincipalIdr, item.approvedPrincipalIdr)})`
              : ""}
          </div>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-right">
        <div>{formatCurrency(item.heldPrincipalIdr)}</div>
        <div className="text-[color:var(--kkp-text-muted)]">
          + {formatCurrency(item.heldFeeIdr)} biaya
        </div>
      </td>
      <td className="py-2 pr-3">
        <div>
          <StagePill stage={item.stage} />
        </div>
        <div className="mt-1 text-[11px] text-[color:var(--kkp-text-muted)]">
          {item.batchStatus}
        </div>
        {item.overdue ? (
          <div className="mt-1">
            <OverdueBadge />
          </div>
        ) : null}
      </td>
      <td className="py-2 pr-3 text-right">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => navigate(`/fds/${item.id}`)}
        >
          Lihat kasus
        </Button>
      </td>
    </tr>
  );
}
